import React, { useEffect, useState } from 'react';
import { ActivityIndicator, ScrollView, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Redirect, router } from 'expo-router';

import { AppointmentCard } from '@/components/schedule/appointment-card';
import { ThemedText } from '@/components/themed-text';
import { PressableScale } from '@/components/ui/pressable-scale';
import {
  clearStoredOtpToken,
  getStoredGuestSession,
  GuestSession,
  useGuestMyAppointments,
} from '@/hooks/use-public-schedule';
import { useAuthStore } from '@/store/authStore';
import { formatCurrency } from '@/utils/format-currency';
import { formatDate } from '@/utils/format-date';
import { recurrenceScheduleLine } from '@/utils/format-recurrence';

const BRAND = '#FD5001';

type PendingRequest = NonNullable<
  ReturnType<typeof useGuestMyAppointments>['data']
>['pendingRequests'];

/**
 * Pedido de assinatura/recorrência esperando o prestador. Não vira agendamento
 * até ser aprovado — sem este card a tela ficaria vazia logo depois de assinar,
 * como se o pedido tivesse se perdido.
 */
function PendingRequestCard({ request }: { request: NonNullable<PendingRequest>[number] }) {
  const lines = request.slots.map((slot) =>
    recurrenceScheduleLine({
      frequency: slot.frequency as never,
      daysOfWeek: slot.daysOfWeek,
      dayOfMonth: slot.dayOfMonth ?? undefined,
      startTime: slot.startTime ?? undefined,
      endTime: slot.endTime ?? undefined,
    } as never),
  );

  return (
    <View
      className="gap-2 rounded-2xl border border-amber-200 bg-amber-50 p-4"
      style={{ borderCurve: 'continuous' }}
    >
      <View className="flex-row items-center gap-2">
        <Ionicons className="text-amber-700" name="hourglass-outline" size={16} />
        <ThemedText className="font-bold text-xs uppercase text-amber-700">
          {request.isSubscription ? 'Assinatura pedida' : 'Recorrência pedida'}
        </ThemedText>
      </View>
      <ThemedText className="font-bold text-base">{request.serviceName}</ThemedText>
      {lines.map((line, index) => (
        <ThemedText key={index} className="text-sm text-neutral-600">
          {line}
        </ThemedText>
      ))}
      <ThemedText className="text-sm text-neutral-600">
        {request.endDate
          ? `${formatDate(request.startDate, 'dd/MM')} até ${formatDate(request.endDate, 'dd/MM')}`
          : `A partir de ${formatDate(request.startDate, 'dd/MM')}`}
        {request.price ? ` · ${formatCurrency(request.price)}` : ''}
      </ThemedText>
      <ThemedText className="text-xs text-amber-700">
        O prestador confirma pelo WhatsApp. Os horários aparecem aqui assim que ele aprovar.
      </ThemedText>
    </View>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <View className="flex-1 items-center justify-center gap-3 bg-neutral-50 p-8">{children}</View>
  );
}

/**
 * Agendamentos do convidado neste device: o telefone verificado (otpToken, 30
 * dias) é a credencial. Sem isso o único caminho de volta seria o link mágico
 * do WhatsApp — que some quando a conversa some.
 */
export default function GuestAppointmentsScreen() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const [session, setSession] = useState<GuestSession | null>(null);
  const [loadingSession, setLoadingSession] = useState(true);

  useEffect(() => {
    void getStoredGuestSession().then((stored) => {
      setSession(stored);
      setLoadingSession(false);
    });
  }, []);

  const { data, isLoading, error } = useGuestMyAppointments(session);

  // Token recusado pelo backend (expirado/revogado): não adianta guardar.
  useEffect(() => {
    if (error && session) {
      void clearStoredOtpToken(session.identity.phone);
    }
  }, [error, session]);

  // Quem tem conta vê os agendamentos na área do cliente, com histórico e tudo.
  if (isAuthenticated) {
    return <Redirect href="/(tabs)/(client)/appointments" />;
  }

  if (loadingSession || (session && isLoading)) {
    return (
      <Centered>
        <ActivityIndicator size="large" color={BRAND} />
      </Centered>
    );
  }

  if (!session || error || data?.requiresLogin) {
    const needsLogin = !!data?.requiresLogin;
    return (
      <Centered>
        <Ionicons
          className="text-neutral-400"
          name={needsLogin ? 'person-circle-outline' : 'calendar-outline'}
          size={48}
        />
        <ThemedText className="text-center font-bold text-xl">
          {needsLogin ? 'Este telefone já tem conta' : 'Nenhum agendamento por aqui'}
        </ThemedText>
        <ThemedText className="text-center text-neutral-500">
          {needsLogin
            ? 'Entre na sua conta para ver seus agendamentos.'
            : 'Confirme seu telefone na página do prestador ao agendar e seus agendamentos aparecem aqui.'}
        </ThemedText>
        {needsLogin && (
          <PressableScale
            onPress={() => router.push('/(auth)/login')}
            className="mt-2 items-center rounded-full px-8 py-4"
            style={{ backgroundColor: BRAND, borderCurve: 'continuous' }}
          >
            <ThemedText className="font-bold text-white">Fazer login</ThemedText>
          </PressableScale>
        )}
      </Centered>
    );
  }

  const appointments = data?.appointments ?? [];
  const pendingRequests = data?.pendingRequests ?? [];

  if (appointments.length === 0 && pendingRequests.length === 0) {
    return (
      <Centered>
        <Ionicons className="text-neutral-400" name="calendar-outline" size={48} />
        <ThemedText className="text-center font-bold text-xl">Nenhum agendamento</ThemedText>
        <ThemedText className="text-center text-neutral-500">
          Quando você agendar um horário, ele aparece aqui.
        </ThemedText>
      </Centered>
    );
  }

  return (
    <ScrollView
      keyboardShouldPersistTaps="handled"
      className="flex-1 bg-neutral-50"
      contentContainerStyle={{ padding: 20, gap: 12 }}
    >
      <View className="gap-1">
        <ThemedText className="font-bold text-2xl">Meus agendamentos</ThemedText>
        <ThemedText className="text-sm text-neutral-500">{session.identity.name}</ThemedText>
      </View>

      {pendingRequests.map((request) => (
        <PendingRequestCard key={request.id} request={request} />
      ))}

      {appointments.map(({ appointment, magicToken }) => (
        <PressableScale
          key={appointment.id}
          onPress={() =>
            router.push({
              pathname: '/agendamento/[appointmentId]',
              params: { appointmentId: appointment.id, token: magicToken },
            })
          }
        >
          <AppointmentCard appointment={appointment} />
        </PressableScale>
      ))}
    </ScrollView>
  );
}
